/**
 * Depth Metrics Module
 * Tracks listening depth metrics for clips (pass counting, early replay detection)
 */

import appState from '../../core/app-state.js';
import { logEvent } from '../../core/event-logger.js';
import { EVENT_TYPES } from '../../core/event-types.js';

/**
 * Initialize depth tracking for a clip
 */
export function initializeClipDepthTracking(clipId: string): void {
  if (!clipId) return;
  
  if (!appState.clipDepth.has(clipId)) {
    appState.clipDepth.set(clipId, {
      passCount: 0,
      earlyReplay: false,
      currentPassStarted: false,
      hasReached50Percent: false,
      lastProgressPercent: 0
    });
  }
}

/**
 * Track depth metrics during playback
 */
export function trackDepthMetrics(audioEl: HTMLAudioElement): void {
  if (!audioEl || !audioEl.duration || !appState.activeClipId) return;
  
  const clipId = appState.activeClipId;
  const currentTime = audioEl.currentTime;
  const duration = audioEl.duration;
  const progressPercent = (currentTime / duration) * 100;
  
  // Initialize tracking if needed
  initializeClipDepthTracking(clipId);
  const depthData = appState.clipDepth.get(clipId);
  if (!depthData) return; // Should not happen after initialization, but needed for type safety
  
  // Track if we've reached 50% for early replay detection
  if (progressPercent >= 50 && !depthData.hasReached50Percent) {
    depthData.hasReached50Percent = true;
  }
  
  // R1: Track pass start when crossing 5% from lower value
  if (progressPercent >= 5 && depthData.lastProgressPercent < 5 && !depthData.currentPassStarted) {
    depthData.currentPassStarted = true;
    
    // Log depth_pass_start event
    logEvent(EVENT_TYPES['DEPTH_PASS_START']!, {
      clipId: clipId,
      passIndex: depthData.passCount,
      timestamp: currentTime
    });
  }
  
  // R2: Track pass completion when reaching 90% after a started pass
  if (progressPercent >= 90 && depthData.currentPassStarted) {
    depthData.passCount++;
    depthData.currentPassStarted = false;
    
    // Log depth_pass_complete event
    logEvent(EVENT_TYPES['DEPTH_PASS_COMPLETE']!, {
      clipId: clipId,
      passIndex: depthData.passCount - 1, // 0-based index for the just-completed pass
      timestamp: currentTime
    });
  }
  
  // Update last progress for next comparison
  depthData.lastProgressPercent = progressPercent;
}

/**
 * Track seeks for early replay detection
 */
export function trackDepthSeek(audioEl: HTMLAudioElement, fromTime: number, toTime: number): void {
  if (!audioEl || !audioEl.duration || !appState.activeClipId) return;
  
  const clipId = appState.activeClipId;
  const duration = audioEl.duration;
  const fromPercent = (fromTime / duration) * 100;
  const toPercent = (toTime / duration) * 100;
  
  // Initialize tracking if needed
  initializeClipDepthTracking(clipId);
  const depthData = appState.clipDepth.get(clipId);
  if (!depthData) return; // Should not happen after initialization, but needed for type safety
  
  // R3: Detect early replay when seeking from ≥5% back to <10% before reaching 50%
  if (fromPercent >= 5 && toPercent < 10 && !depthData.hasReached50Percent && !depthData.earlyReplay) {
    depthData.earlyReplay = true;
    
    // Log early_replay_detected event
    logEvent(EVENT_TYPES['EARLY_REPLAY_DETECTED']!, {
      clipId: clipId,
      fromTime: fromTime,
      toTime: toTime,
      fromPercent: fromPercent,
      toPercent: toPercent
    });
  }
  
  // Update last progress for metrics tracking
  depthData.lastProgressPercent = toPercent;
}
